/**
 * Multiple knowledge bases per org (beta).
 *
 * The monolith only exposes org-scoped articles with collectionKeys. There is no
 * knowledge-bases API, so Agents matches One:
 * - A reserved collection key prefix `kb:<id>` on articles to scope membership.
 * - An org-scoped API registry (GET/POST/PATCH /api/knowledge/registry/*) for names
 *   and the active base (see knowledgeRegistryCrossSync.ts).
 * - An optional draft "anchor" article (created on New knowledge base) so empty
 *   bases still show up when discovering `kb:` keys from GET /api/knowledge/articles.
 *
 * Orgs start with zero knowledge bases. The company creates the first via
 * New knowledge base. Suite and custom books stay optional inside each base.
 */

import { publishKnowledgeRegistryForOrg } from "@/lib/knowledgeRegistryCrossSync";

export type KnowledgeBaseRecord = {
  id: string;
  name: string;
  /** Optional blurb for lists and the active base header. */
  description?: string;
  createdAt: string;
};

/** Reserved id; never auto-created. Kept so old client data cannot collide. */
export const DEFAULT_KNOWLEDGE_BASE_ID = "default";

const STORAGE_PREFIX = "atw-one.knowledge.bases";
const ACTIVE_PREFIX = "atw-one.knowledge.activeBase";
export const KNOWLEDGE_BASES_CHANGED = "atw-one:knowledge-bases-changed";

const KB_KEY_PREFIX = "kb:";

export const KNOWLEDGE_BASE_ANCHOR_MARKER = "<!-- atw-one:knowledge-base -->";

function basesStorageKey(orgId: string): string {
  return `${STORAGE_PREFIX}.${orgId || "default"}`;
}

function activeStorageKey(orgId: string): string {
  return `${ACTIVE_PREFIX}.${orgId || "default"}`;
}

function isBaseRecord(value: unknown): value is KnowledgeBaseRecord {
  if (!value || typeof value !== "object") return false;
  const row = value as KnowledgeBaseRecord;
  return (
    typeof row.id === "string" &&
    row.id.trim().length > 0 &&
    typeof row.name === "string" &&
    row.name.trim().length > 0 &&
    typeof row.createdAt === "string" &&
    (row.description === undefined || typeof row.description === "string")
  );
}

function sortBases(bases: KnowledgeBaseRecord[]): KnowledgeBaseRecord[] {
  return [...bases].sort((a, b) => a.name.localeCompare(b.name));
}

export function kbMembershipKey(kbId: string): string {
  return `${KB_KEY_PREFIX}${kbId.trim()}`;
}

export function isKbMembershipKey(key: string): boolean {
  return key.startsWith(KB_KEY_PREFIX) && key.length > KB_KEY_PREFIX.length;
}

export function parseKbMembershipId(key: string): string | null {
  if (!isKbMembershipKey(key)) return null;
  return key.slice(KB_KEY_PREFIX.length).trim() || null;
}

/** Book / library keys only (hide kb membership from those UIs). */
export function isBookCollectionKey(key: string): boolean {
  return !isKbMembershipKey(key);
}

export function stripKbMembershipKeys(keys: string[]): string[] {
  return keys.filter(isBookCollectionKey);
}

export function withKbMembership(keys: string[], kbId: string): string[] {
  const without = stripKbMembershipKeys(keys);
  const id = kbId.trim();
  if (!id || id === DEFAULT_KNOWLEDGE_BASE_ID) return without;
  const membership = kbMembershipKey(id);
  return Array.from(new Set([...without, membership])).sort();
}

/** Drop one kb:<id> membership key; leave book keys and other kb: keys alone. */
export function withoutKbMembership(keys: string[], kbId: string): string[] {
  const membership = kbMembershipKey(kbId);
  return keys.filter((key) => key !== membership);
}

export function articleBelongsToKnowledgeBase(
  article: { collectionKeys: string[] },
  kbId: string,
): boolean {
  const id = kbId.trim();
  if (!id) return false;
  const kbKeys = article.collectionKeys.filter(isKbMembershipKey);
  if (id === DEFAULT_KNOWLEDGE_BASE_ID) {
    return kbKeys.length === 0 || kbKeys.includes(kbMembershipKey(DEFAULT_KNOWLEDGE_BASE_ID));
  }
  return kbKeys.includes(kbMembershipKey(id));
}

export function suggestKnowledgeBaseId(name: string): string {
  const slug = name
    .trim()
    .toLowerCase()
    .normalize("NFKD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 40);
  const suffix =
    typeof crypto !== "undefined" && "randomUUID" in crypto
      ? crypto.randomUUID().replace(/-/g, "").slice(0, 8)
      : `${Date.now().toString(36)}`;
  const base = slug && slug !== DEFAULT_KNOWLEDGE_BASE_ID ? slug : "knowledge-base";
  return `${base}-${suffix}`;
}

/** True when the stored label is only the opaque id (resync fallout). */
export function knowledgeBaseNameIsIdFallback(name: string, id: string): boolean {
  return name.trim() === id.trim();
}

/**
 * Human label from a generated id like `customer-09d9122d` → `Customer`.
 * Strips the 8-hex suffix from suggestKnowledgeBaseId, then title-cases the stem.
 */
export function displayNameFromKnowledgeBaseId(id: string): string {
  const trimmed = id.trim();
  if (!trimmed) return "Knowledge base";
  const withoutSuffix = trimmed.replace(/-[a-f0-9]{8}$/i, "");
  const stem = (withoutSuffix || trimmed).replace(/-+/g, " ").trim();
  if (!stem) return trimmed;
  return stem.replace(/\b[a-z]/g, (ch) => ch.toUpperCase());
}

/**
 * Prefer a real display name over the raw id. Order: non-id name, article title,
 * title-cased id stem, then id.
 */
export function resolveKnowledgeBaseDisplayName(input: {
  id: string;
  name?: string;
  articleTitle?: string;
}): string {
  const id = input.id.trim();
  const name = (input.name || "").trim();
  const title = (input.articleTitle || "").trim();
  if (name && !knowledgeBaseNameIsIdFallback(name, id)) return name;
  if (title && !knowledgeBaseNameIsIdFallback(title, id)) return title;
  const fromId = displayNameFromKnowledgeBaseId(id);
  return fromId || id || "Knowledge base";
}

/** List/header label for a registry row (never present the raw id when a stem exists). */
export function knowledgeBaseLabel(base: Pick<KnowledgeBaseRecord, "id" | "name">): string {
  return resolveKnowledgeBaseDisplayName({ id: base.id, name: base.name });
}

export function knowledgeBaseAnchorMarkdown(name: string): string {
  return `${KNOWLEDGE_BASE_ANCHOR_MARKER}

This draft marks the **${name.trim() || "knowledge base"}** knowledge base. Replace this text with a welcome article whenever you like. Libraries, books, and other articles you add while this base is selected stay scoped here.
`;
}

function readStoredBases(orgId: string | null | undefined): KnowledgeBaseRecord[] {
  if (typeof window === "undefined") return [];
  try {
    const raw = window.localStorage.getItem(basesStorageKey(orgId || "default"));
    if (!raw) return [];
    const parsed = JSON.parse(raw) as unknown;
    if (!Array.isArray(parsed)) return [];
    const byId = new Map<string, KnowledgeBaseRecord>();
    for (const item of parsed) {
      if (!isBaseRecord(item)) continue;
      const id = item.id.trim();
      if (!id || id === DEFAULT_KNOWLEDGE_BASE_ID) continue;
      const description = (item.description || "").trim();
      byId.set(id, {
        id,
        name: item.name.trim(),
        ...(description ? { description } : {}),
        createdAt: item.createdAt,
      });
    }
    return sortBases([...byId.values()]);
  } catch {
    return [];
  }
}

function writeStoredBases(orgId: string | null | undefined, bases: KnowledgeBaseRecord[]): void {
  if (typeof window === "undefined") return;
  try {
    const clean = bases.filter((base) => base.id !== DEFAULT_KNOWLEDGE_BASE_ID);
    window.localStorage.setItem(basesStorageKey(orgId || "default"), JSON.stringify(clean));
    window.dispatchEvent(new CustomEvent(KNOWLEDGE_BASES_CHANGED));
    publishKnowledgeRegistryForOrg(orgId);
  } catch {
    /* ignore quota / private mode */
  }
}

export function listKnowledgeBases(orgId: string | null | undefined): KnowledgeBaseRecord[] {
  return readStoredBases(orgId);
}

/** Active base id, or "" when the org has none / none selected. */
export function getActiveKnowledgeBaseId(orgId: string | null | undefined): string {
  if (typeof window === "undefined") return "";
  try {
    const bases = listKnowledgeBases(orgId);
    if (bases.length === 0) return "";
    const raw = window.localStorage.getItem(activeStorageKey(orgId || "default"));
    const id = (raw || "").trim();
    if (id && bases.some((base) => base.id === id)) return id;
    return bases[0]?.id || "";
  } catch {
    return "";
  }
}

export function setActiveKnowledgeBaseId(
  orgId: string | null | undefined,
  kbId: string,
): void {
  if (typeof window === "undefined") return;
  const id = kbId.trim();
  try {
    if (!id) {
      window.localStorage.removeItem(activeStorageKey(orgId || "default"));
    } else {
      window.localStorage.setItem(activeStorageKey(orgId || "default"), id);
    }
    window.dispatchEvent(new CustomEvent(KNOWLEDGE_BASES_CHANGED));
    publishKnowledgeRegistryForOrg(orgId);
  } catch {
    /* ignore */
  }
}

export function upsertKnowledgeBase(
  orgId: string | null | undefined,
  input: { name: string; id?: string; description?: string },
): { ok: true; base: KnowledgeBaseRecord } | { ok: false; error: string } {
  const name = input.name.trim();
  if (!name) return { ok: false, error: "Name is required." };

  let id = (input.id || "").trim();
  if (!id) id = suggestKnowledgeBaseId(name);
  if (id === DEFAULT_KNOWLEDGE_BASE_ID) {
    return { ok: false, error: "That id is reserved. Choose a different name." };
  }
  if (id.includes(":") || /\s/.test(id)) {
    return { ok: false, error: "Use a simple id without spaces or colons." };
  }

  const existing = listKnowledgeBases(orgId);
  const previous = existing.find((base) => base.id === id);
  const descriptionInput =
    input.description !== undefined ? input.description.trim() : (previous?.description || "").trim();
  const base: KnowledgeBaseRecord = {
    id,
    name,
    ...(descriptionInput ? { description: descriptionInput } : {}),
    createdAt: previous?.createdAt || new Date().toISOString(),
  };
  const next = sortBases([...existing.filter((row) => row.id !== id), base]);
  writeStoredBases(orgId, next);
  return { ok: true, base };
}

/** Update name and/or description. Id (and kb:<id> membership) stay the same. */
export function updateKnowledgeBase(
  orgId: string | null | undefined,
  kbId: string,
  input: { name: string; description?: string },
): { ok: true; base: KnowledgeBaseRecord } | { ok: false; error: string } {
  const id = kbId.trim();
  if (!id || id === DEFAULT_KNOWLEDGE_BASE_ID) {
    return { ok: false, error: "That knowledge base cannot be edited." };
  }
  const existing = listKnowledgeBases(orgId).find((base) => base.id === id);
  if (!existing) return { ok: false, error: "Knowledge base not found." };
  return upsertKnowledgeBase(orgId, {
    id,
    name: input.name,
    description: input.description !== undefined ? input.description : existing.description || "",
  });
}

/** Rename an existing base. Id (and kb:<id> membership) stay the same. */
export function renameKnowledgeBase(
  orgId: string | null | undefined,
  kbId: string,
  name: string,
): { ok: true; base: KnowledgeBaseRecord } | { ok: false; error: string } {
  return updateKnowledgeBase(orgId, kbId, { name });
}

/**
 * Remove a base from the local registry and clear the active selection when needed.
 * Does not touch articles; callers strip kb:<id> membership separately so discovery
 * cannot resurrect the base.
 */
export function removeKnowledgeBase(
  orgId: string | null | undefined,
  kbId: string,
): { ok: true } | { ok: false; error: string } {
  const id = kbId.trim();
  if (!id || id === DEFAULT_KNOWLEDGE_BASE_ID) {
    return { ok: false, error: "That knowledge base cannot be deleted." };
  }
  const existing = listKnowledgeBases(orgId);
  if (!existing.some((base) => base.id === id)) {
    return { ok: false, error: "Knowledge base not found." };
  }
  const next = existing.filter((base) => base.id !== id);
  let storedActive = "";
  if (typeof window !== "undefined") {
    try {
      storedActive = (window.localStorage.getItem(activeStorageKey(orgId || "default")) || "").trim();
    } catch {
      storedActive = "";
    }
  }
  writeStoredBases(orgId, next);
  if (storedActive === id || !next.some((base) => base.id === storedActive)) {
    setActiveKnowledgeBaseId(orgId, next[0]?.id || "");
  }
  return { ok: true };
}

/**
 * Upgrade display names for already-listed bases from article titles.
 * Does not invent new bases from kb:<id> keys (that resurrected deleted bases).
 * New bases come from the Knowledge registry API.
 */
export function mergeDiscoveredKnowledgeBases(
  orgId: string | null | undefined,
  articles: {
    title?: string;
    collectionKeys: string[];
    createdAt?: string;
    updatedAt?: string;
  }[],
): { bases: KnowledgeBaseRecord[]; changed: boolean } {
  const existing = listKnowledgeBases(orgId);
  const byId = new Map(existing.map((base) => [base.id, base]));
  let discovered = false;

  for (const article of articles) {
    for (const key of article.collectionKeys) {
      const id = parseKbMembershipId(key);
      if (!id || id === DEFAULT_KNOWLEDGE_BASE_ID) continue;
      const title = (article.title || "").trim();
      const row = byId.get(id);
      if (!row) continue;
      if (
        knowledgeBaseNameIsIdFallback(row.name, id) &&
        title &&
        !knowledgeBaseNameIsIdFallback(title, id)
      ) {
        discovered = true;
        byId.set(id, { ...row, name: title });
      }
    }
  }

  const next = sortBases([...byId.values()]);
  if (discovered) writeStoredBases(orgId, next);
  return { bases: next, changed: discovered };
}

export function getKnowledgeBaseName(
  orgId: string | null | undefined,
  kbId: string,
): string {
  const match = listKnowledgeBases(orgId).find((base) => base.id === kbId);
  if (match) return knowledgeBaseLabel(match);
  return resolveKnowledgeBaseDisplayName({ id: kbId });
}
